import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Alert } from '@/design-system/alert';
import { Badge } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import { Field } from '@/design-system/field';
import { FormModal } from '@/design-system/form-modal';
import { Input, InputRoot } from '@/design-system/input';
import { Label } from '@/design-system/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/select';
import { Textarea } from '@/design-system/textarea';
import { toast } from '@/design-system/toaster';
import {
  ForceTerminalAction,
  Shipment,
  ShipmentApiError,
  ShipmentReason,
  ShipmentRoute,
  ShipmentRouteField,
  shipmentsAPI,
} from '@/services/api/shipments';
import { isFileFieldType } from '@/services/api/stopWorkflows';

import { EvidenceImageError, prepareEvidenceFile } from './evidenceImage';
import {
  buildValues,
  fieldStatus,
  MilestoneDraft,
  MilestoneStatus,
  milestoneStatus,
  missingSignatures,
  PendingPhotoMilestone,
  pendingPhotoMilestones,
  requiredPhotoCount,
  ResolvedStopInfo,
  resolvedStopInfo,
} from './opsEvidence';
import {
  dropFailureSummary,
  RouteEndpoints,
  routeEndpoints,
  shipmentSettlesFailed,
} from './stopFailure';

interface ForceTerminalModalProps {
  /** The shipment to override; when null the modal is closed. */
  shipment: Shipment | null;
  /** COMPLETE → Mark Completed, FAIL → Mark Failed. */
  action: ForceTerminalAction;
  open: boolean;
  onClose: () => void;
  /** Called with the refreshed shipment after a successful override. */
  onDone: (updated: Shipment) => void;
  /**
   * Swap the modal to the other action (the 7C "all drops failed" banner
   * offers Mark failed). When omitted the banner shows without the link.
   */
  onSwitchAction?: (action: ForceTerminalAction) => void;
}

const COPY: Record<
  ForceTerminalAction,
  { title: string; verb: string; result: string; icon: string }
> = {
  COMPLETE: {
    title: 'Mark shipment completed?',
    verb: 'Mark completed',
    result: 'completed',
    icon: 'ri-checkbox-circle-line',
  },
  FAIL: {
    title: 'Mark shipment failed?',
    verb: 'Mark failed',
    result: 'failed',
    icon: 'ri-error-warning-line',
  },
};

/**
 * The reason is a free "Other" when its code is the OTHER sentinel or its label
 * *starts* with Lainnya / Other. The leading-anchor avoids a false positive on
 * labels like "…via channel lain" that merely contain the word.
 */
const isOtherReason = (reason?: ShipmentReason): boolean =>
  !!reason &&
  (reason.code === 'OVERRIDE_OTHER' || /^\s*(lain|other)/i.test(reason.label));

/** Evidence photos / typed values keyed by `${stopId}::${milestoneKey}::${fieldKey}`. */
type PhotoMap = Record<string, string[]>;
type TextMap = Record<string, string>;

const milestonePrefix = (stopId: string, milestoneKey: string) =>
  `${stopId}::${milestoneKey}::`;
const slotKey = (stopId: string, milestoneKey: string, fieldKey: string) =>
  `${milestonePrefix(stopId, milestoneKey)}${fieldKey}`;

/** Milestones saved from this modal while it stays open (4A strip). */
interface SavedEntry {
  key: string;
  name: string;
  at: Date;
}

const hhmm = (d: Date) =>
  d.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });

const INCOMPLETE_HINT_ID = 'force-terminal-incomplete-hint';
const SIGNATURE_LOCK_ID = 'force-terminal-signature-lock';

/** Design 2F copy — English, like the rest of this modal. */
const SIGNATURE_COPY = {
  chip: 'Evidence incomplete',
  slot: 'Upload 1 photo/scan of the signed delivery note (JPEG/PNG)',
  lock: "Without a signature this stop can't be completed. Ask the driver for a photo of the delivery note, or use Mark failed.",
};
/** A signature slot takes a JPEG/PNG photo or scan of the delivery note (D4). */
const SIGNATURE_ACCEPT = 'image/jpeg,image/png';

const NO_ENDPOINTS: RouteEndpoints = { origin: null, destination: null };

/**
 * The OPS force-terminal override — force an in-flight shipment to COMPLETED or
 * FAILED. `reasonCode` (an `OPS_OVERRIDE` code from `GET /v1/reasons`) is
 * required; the reason's label is sent as the backend's mandatory
 * `justification`, except when "Other/Lainnya" is picked, where the operator
 * types a free-text note instead. Backend 400s (`INVALID_STATE` /
 * `INVALID_REASON`) surface as a toast.
 *
 * A DIRECT_4W COMPLETE also loads the route's workflow so ops can supply the
 * milestone evidence the rider could not. Evidence follows the frozen snapshot
 * exactly (photo count per field, required text/number — see opsEvidence.ts):
 *
 *   "Save evidence only" ─► PATCH ops-evidence for each complete milestone
 *                           (409 already-submitted counts as saved) ─► re-fetch
 *                           the route; never force-terminal.
 *   "Mark completed"     ─► enabled only when every pending photo milestone is
 *                           complete ─► same save pass ─► POST force-terminal.
 *
 * A stop the driver FAILED or Ops CANCELLED is never asked for photos (the BE
 * gate ignores both); it shows as an info row "Stop N · Failed by driver ·
 * <reason>" / "Stop N · Cancelled by Ops · <reason>". When no counted drop can
 * still be delivered (each FAILED, CANCELLED or skipped because its pickup
 * failed), a warning points ops at Mark failed — the shipment would settle
 * FAILED on its own — without blocking Mark completed.
 *
 * A FAIL carries no workflow evidence.
 */
const ForceTerminalModal: React.FC<ForceTerminalModalProps> = ({
  shipment,
  action,
  open,
  onClose,
  onDone,
  onSwitchAction,
}) => {
  const [reasons, setReasons] = useState<ShipmentReason[]>([]);
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [reasonCode, setReasonCode] = useState<string>('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);

  // DIRECT_4W workflow evidence.
  const [route, setRoute] = useState<ShipmentRoute | null>(null);
  // First pickup / last drop coordinates, for the return-to-origin rule.
  const [endpoints, setEndpoints] = useState<RouteEndpoints>(NO_ENDPOINTS);
  const [loadingRoute, setLoadingRoute] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [photos, setPhotos] = useState<PhotoMap>({});
  const [texts, setTexts] = useState<TextMap>({});
  // In-flight uploads per slot, so submit can block until all resolve, plus
  // what the in-flight tile says ("Mengompres…" / "Mengunggah…").
  const [uploading, setUploading] = useState<Record<string, number>>({});
  const [phase, setPhase] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [failedFiles, setFailedFiles] = useState<Record<string, File[]>>({});
  const [trimHints, setTrimHints] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState<SavedEntry[]>([]);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const savedStripRef = useRef<HTMLDivElement>(null);

  const copy = COPY[action];
  const is4W = shipment?.type === 'DIRECT_4W';
  // Workflow evidence is only for a 4W COMPLETE — a FAIL doesn't collect POD.
  const showEvidence = is4W && action === 'COMPLETE';

  const selectedReason = reasons.find((r) => r.code === reasonCode);
  const isOther = isOtherReason(selectedReason);

  useEffect(() => {
    if (!open) {
      setReasonCode('');
      setNote('');
      setRoute(null);
      setEndpoints(NO_ENDPOINTS);
      setLoadError(false);
      setPhotos({});
      setTexts({});
      setUploading({});
      setPhase({});
      setFieldErrors({});
      setFailedFiles({});
      setTrimHints({});
      setSaved([]);
      setExpandedKey(null);
      return;
    }
    let cancelled = false;
    setLoadingReasons(true);
    shipmentsAPI
      .listReasons('OPS_OVERRIDE')
      .then((rows) => {
        if (!cancelled) setReasons(rows);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setReasons([]);
        toast.error('Could not load override reasons', {
          description: err instanceof Error ? err.message : 'Unknown error',
        });
      })
      .finally(() => {
        if (!cancelled) setLoadingReasons(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  /** (Re)load the route; the list record does not carry the workflow. */
  const loadRoute = useCallback(async () => {
    if (!shipment) return;
    setLoadingRoute(true);
    setLoadError(false);
    try {
      const full = await shipmentsAPI.get(shipment.id);
      setRoute(full.route ?? null);
      setEndpoints(routeEndpoints(full));
    } catch {
      setRoute(null);
      setLoadError(true);
    } finally {
      setLoadingRoute(false);
    }
  }, [shipment]);

  useEffect(() => {
    if (!open || !showEvidence || !shipment) {
      setRoute(null);
      return;
    }
    let cancelled = false;
    setLoadingRoute(true);
    setLoadError(false);
    shipmentsAPI
      .get(shipment.id)
      .then((full) => {
        if (cancelled) return;
        setRoute(full.route ?? null);
        setEndpoints(routeEndpoints(full));
      })
      .catch(() => {
        if (cancelled) return;
        setRoute(null);
        setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoadingRoute(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, showEvidence, shipment]);

  /**
   * Every photo milestone on the route the driver has NOT submitted, across
   * every stop. "Mark completed" is earned: the backend refuses COMPLETE while
   * any photo milestone is pending (422 EVIDENCE_INCOMPLETE), so ops must
   * supply all of them here — nothing is auto-skipped on a completed shipment.
   * Milestones the driver already captured are simply not asked for again,
   * and a stop the driver FAILED or Ops CANCELLED is not asked for at all.
   */
  const pending: PendingPhotoMilestone[] = useMemo(
    () => (route ? pendingPhotoMilestones(route.stops) : []),
    [route]
  );

  /** Stops failed by the driver or cancelled by Ops — info rows, no slots. */
  const resolvedStops: ResolvedStopInfo[] = useMemo(
    () => (route ? resolvedStopInfo(route.stops) : []),
    [route]
  );

  // 7C: no counted drop can still be delivered — the server would settle this
  // shipment FAILED at end-route, so Mark completed is probably wrong.
  const allDropsFailed = useMemo(
    () =>
      !!route &&
      shipmentSettlesFailed(dropFailureSummary(route.stops, endpoints)),
    [route, endpoints]
  );

  const draftFor = useCallback(
    (pm: PendingPhotoMilestone): MilestoneDraft => {
      const draft: MilestoneDraft = { photos: {}, texts: {} };
      for (const f of pm.fields) {
        const key = slotKey(pm.stopId, pm.milestoneKey, f.fieldKey);
        draft.photos[f.fieldKey] = photos[key] ?? [];
        if (texts[key] !== undefined) draft.texts[f.fieldKey] = texts[key];
      }
      return draft;
    },
    [photos, texts]
  );

  const statuses: MilestoneStatus[] = useMemo(
    () => pending.map((pm) => milestoneStatus(pm.fields, draftFor(pm))),
    [pending, draftFor]
  );
  const completeCount = statuses.filter((s) => s === 'complete').length;
  const incompleteCount = pending.length - completeCount;
  /** Per pending milestone: is a required signature still without its photo? */
  const signatureMissing: boolean[] = useMemo(
    () =>
      pending.map((pm) => missingSignatures(pm.fields, draftFor(pm)).length > 0),
    [pending, draftFor]
  );

  const anyUploading = useMemo(
    () => Object.values(uploading).some((n) => n > 0),
    [uploading]
  );

  /** Compress, upload and attach files to one photo slot, one at a time. */
  const processFiles = async (key: string, files: File[]) => {
    if (!shipment || files.length === 0) return;
    setFieldErrors((p) => ({ ...p, [key]: '' }));
    setFailedFiles((p) => ({ ...p, [key]: [] }));
    setUploading((p) => ({ ...p, [key]: (p[key] ?? 0) + files.length }));
    for (const file of files) {
      try {
        setPhase((p) => ({ ...p, [key]: 'Mengompres…' }));
        const ready = await prepareEvidenceFile(file);
        setPhase((p) => ({ ...p, [key]: 'Mengunggah…' }));
        const url = await shipmentsAPI.uploadEvidence(shipment.id, ready);
        setPhotos((p) => ({ ...p, [key]: [...(p[key] ?? []), url] }));
      } catch (err) {
        if (err instanceof EvidenceImageError) {
          // A format/size problem: retrying the same file cannot help.
          setFieldErrors((p) => ({ ...p, [key]: err.message }));
        } else {
          setFieldErrors((p) => ({
            ...p,
            [key]: `Gagal mengunggah foto. ${
              err instanceof Error ? err.message : ''
            }`.trim(),
          }));
          setFailedFiles((p) => ({ ...p, [key]: [...(p[key] ?? []), file] }));
        }
      } finally {
        setUploading((p) => ({ ...p, [key]: Math.max(0, (p[key] ?? 1) - 1) }));
      }
    }
  };

  const handleFiles = (
    key: string,
    field: ShipmentRouteField,
    fileList: FileList | null
  ) => {
    if (!fileList || fileList.length === 0) return;
    // Copy out of the live FileList now: the input's onChange clears it
    // (`value = ''`) before React runs the state updaters below.
    const picked = Array.from(fileList);
    const need = requiredPhotoCount(field);
    const room = Math.max(0, need - (photos[key]?.length ?? 0));
    const files = picked.slice(0, room);
    const trimmed = picked.length > room;
    setTrimHints((p) => ({
      ...p,
      [key]: trimmed ? `Hanya ${need} foto dipakai` : '',
    }));
    void processFiles(key, files);
  };

  const retryFailed = (key: string) =>
    void processFiles(key, failedFiles[key] ?? []);

  const removePhoto = (key: string, url: string) => {
    setPhotos((p) => ({
      ...p,
      [key]: (p[key] ?? []).filter((u) => u !== url),
    }));
    setTrimHints((p) => ({ ...p, [key]: '' }));
  };

  const setText = (key: string, value: string) =>
    setTexts((p) => ({ ...p, [key]: value }));

  /**
   * Submit every complete milestone. A 409 MILESTONE_ALREADY_SUBMITTED means
   * it is already on the server (a retry, or the driver got there first) and
   * counts as saved. Any other failure stops the pass. Either way the route is
   * re-fetched so the pending list matches the server.
   */
  const savePass = async (): Promise<{ saved: number; failed: boolean }> => {
    if (!route) return { saved: 0, failed: false };
    const done: PendingPhotoMilestone[] = [];
    let failed = false;
    for (let i = 0; i < pending.length; i += 1) {
      if (statuses[i] !== 'complete') continue;
      const pm = pending[i];
      try {
        await shipmentsAPI.submitOpsMilestoneEvidence(
          route.routeId,
          pm.stopId,
          pm.milestoneKey,
          buildValues(pm.fields, draftFor(pm))
        );
        done.push(pm);
      } catch (err) {
        if (
          err instanceof ShipmentApiError &&
          err.code === 'MILESTONE_ALREADY_SUBMITTED'
        ) {
          done.push(pm);
          continue;
        }
        failed = true;
        toast.error('Gagal menyimpan bukti', {
          description: `${pm.milestoneName}: ${
            err instanceof Error ? err.message : 'Unknown error'
          }`,
        });
        break;
      }
    }
    if (done.length > 0) {
      const now = new Date();
      const prefixes = done.map((pm) =>
        milestonePrefix(pm.stopId, pm.milestoneKey)
      );
      const drop = <T,>(m: Record<string, T>) =>
        Object.fromEntries(
          Object.entries(m).filter(
            ([k]) => !prefixes.some((p) => k.startsWith(p))
          )
        );
      setPhotos((p) => drop(p));
      setTexts((p) => drop(p));
      setSaved((prev) => [
        ...prev,
        ...done.map((pm) => ({
          key: milestonePrefix(pm.stopId, pm.milestoneKey),
          name: pm.milestoneName,
          at: now,
        })),
      ]);
    }
    await loadRoute();
    return { saved: done.length, failed };
  };

  const reasonOk =
    reasonCode.length > 0 && (!isOther || note.trim().length > 0);

  // For a 4W COMPLETE every pending photo milestone must be complete — the BE
  // refuses COMPLETE otherwise; partial progress goes through "Save evidence only".
  const evidenceSatisfied =
    !showEvidence || (!loadingRoute && incompleteCount === 0);

  const busy = anyUploading || submitting || saving;
  const canSubmit = reasonOk && evidenceSatisfied && !busy;
  const canSave = showEvidence && completeCount > 0 && !busy;
  const showIncompleteHint =
    showEvidence && !loadingRoute && incompleteCount > 0;
  // A missing required signature already keeps the milestone incomplete (and so
  // Mark completed disabled); this line says why and what ops can do instead.
  const showSignatureLock =
    showEvidence && !loadingRoute && signatureMissing.some(Boolean);
  const submitHintIds =
    [
      showSignatureLock ? SIGNATURE_LOCK_ID : null,
      showIncompleteHint ? INCOMPLETE_HINT_ID : null,
    ]
      .filter(Boolean)
      .join(' ') || undefined;

  const handleClose = () => {
    if (submitting || saving || anyUploading) return;
    onClose();
  };

  const handleSaveOnly = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const { saved: n } = await savePass();
      if (n > 0) {
        toast.success(`${n} milestone tersimpan`);
        requestAnimationFrame(() => savedStripRef.current?.focus());
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!shipment || !canSubmit) return;
    // The BE requires a justification. A coded reason sends its own label;
    // "Other/Lainnya" sends the operator's free-text note.
    const justification = isOther
      ? note.trim()
      : (selectedReason?.label ?? reasonCode);

    setSubmitting(true);
    try {
      if (showEvidence && route && pending.length > 0) {
        const { failed } = await savePass();
        if (failed) return;
      }

      const updated = await shipmentsAPI.forceTerminal(shipment.id, {
        action,
        reasonCode,
        justification,
      });
      toast.success(`Shipment marked ${copy.result}`, {
        description: `${
          updated.waybill || updated.bookingId
        } was force-${copy.result} by OPS override.`,
      });
      onDone(updated);
      onClose();
    } catch (err) {
      toast.error(`Could not mark shipment ${copy.result}`, {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const reasonField = (
    <Field>
      <Label htmlFor="override-reason" required={!showEvidence}>
        {showEvidence
          ? 'Alasan override (wajib untuk Mark completed)'
          : 'Reason'}
      </Label>
      <Select
        value={reasonCode || undefined}
        onValueChange={setReasonCode}
        disabled={loadingReasons || submitting}
      >
        <SelectTrigger id="override-reason" aria-label="Override reason">
          <SelectValue
            placeholder={
              loadingReasons ? 'Loading reasons…' : 'Select a reason…'
            }
          />
        </SelectTrigger>
        <SelectContent>
          {reasons.map((r) => (
            <SelectItem key={r.code} value={r.code}>
              {r.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );

  const noteField = isOther && (
    <Field>
      <Label htmlFor="override-note" required>
        Catatan
      </Label>
      <Textarea
        id="override-note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        maxLength={1000}
        rows={3}
        placeholder="Jelaskan alasan override ini…"
        disabled={submitting}
        className="resize-none"
      />
    </Field>
  );

  return (
    <FormModal
      open={open}
      onClose={handleClose}
      icon={<i className={copy.icon} />}
      iconTone={action === 'COMPLETE' ? 'success' : 'danger'}
      title={copy.title}
      subtitle={shipment?.waybill || shipment?.bookingId}
      size={showEvidence ? 'md' : 'sm'}
      closeOnBackdropClick={!busy}
      footer={
        // Hint on its own line: in one row with three buttons the md modal is
        // too narrow and the kit Button labels wrapped ("Save / evidence /
        // only"). Labels never wrap; on a phone whole buttons wrap instead.
        <div className="flex flex-col items-end gap-2">
          {showSignatureLock && (
            <span
              id={SIGNATURE_LOCK_ID}
              className="max-w-md text-right text-[12px] text-(--state-error-dark)"
            >
              {SIGNATURE_COPY.lock}
            </span>
          )}
          {showIncompleteHint && (
            <span
              id={INCOMPLETE_HINT_ID}
              className="text-[12px] tabular-nums text-(--state-warning-dark)"
            >
              {incompleteCount} milestone belum lengkap
            </span>
          )}
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              tone="neutral"
              style="stroke"
              onClick={handleClose}
              disabled={busy}
              className="whitespace-nowrap"
            >
              Cancel
            </Button>
            {showEvidence && (
              <Button
                tone="primary"
                style="stroke"
                onClick={handleSaveOnly}
                disabled={!canSave}
                loading={saving}
                className="whitespace-nowrap"
              >
                Save evidence only
              </Button>
            )}
            <Button
              tone={action === 'COMPLETE' ? 'primary' : 'destructive'}
              onClick={handleSubmit}
              disabled={!canSubmit}
              loading={submitting}
              aria-describedby={submitHintIds}
              className="whitespace-nowrap"
            >
              {copy.verb}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-text-sub-600">
          This forces the shipment to <b>{copy.result}</b>, bypassing the normal
          lifecycle. Record why below — this override is audited.
        </p>

        {!showEvidence && reasonField}
        {!showEvidence && noteField}

        {showEvidence && !loadingRoute && allDropsFailed && (
          <Alert
            status="warning"
            appearance="lighter"
            size="lg"
            data-testid="force-terminal-all-drops-failed"
            title="No drop can still be delivered: each one failed or was cancelled. Mark failed is usually the right choice."
            action={
              onSwitchAction ? (
                <Button
                  tone="neutral"
                  style="stroke"
                  size="xs"
                  disabled={busy}
                  onClick={() => onSwitchAction('FAIL')}
                  className="whitespace-nowrap"
                >
                  Switch to Mark failed
                </Button>
              ) : undefined
            }
          />
        )}

        {showEvidence && pending.length > 0 && (
          <p className="rounded-gsm border border-stroke-soft-200 bg-bg-weak-50 px-3 py-2 text-[12px] text-text-sub-600">
            Semua milestone foto yang belum dikirim driver wajib diunggah di
            sini ({pending.length} milestone) — mark complete ditolak jika masih
            ada yang kosong.
          </p>
        )}

        {showEvidence && (
          <EvidenceSection
            loading={loadingRoute}
            loadError={loadError}
            onReload={loadRoute}
            pending={pending}
            resolvedStops={resolvedStops}
            statuses={statuses}
            signatureMissing={signatureMissing}
            photos={photos}
            texts={texts}
            uploading={uploading}
            phase={phase}
            fieldErrors={fieldErrors}
            failedFiles={failedFiles}
            trimHints={trimHints}
            saved={saved}
            savedStripRef={savedStripRef}
            expandedKey={expandedKey}
            onToggle={(key) => setExpandedKey((k) => (k === key ? null : key))}
            disabled={submitting || saving}
            onFiles={handleFiles}
            onRetry={retryFailed}
            onRemove={removePhoto}
            onText={setText}
          />
        )}

        {showEvidence && reasonField}
        {showEvidence && noteField}
      </div>
    </FormModal>
  );
};

const MILESTONE_BADGE: Record<
  MilestoneStatus,
  { status: 'neutral' | 'warning' | 'success'; label: string }
> = {
  empty: { status: 'neutral', label: 'Kosong' },
  partial: { status: 'warning', label: 'Sebagian' },
  complete: { status: 'success', label: 'Lengkap ✓' },
};

interface EvidenceSectionProps {
  loading: boolean;
  loadError: boolean;
  onReload: () => void;
  pending: PendingPhotoMilestone[];
  /** Stops failed by the driver / cancelled by Ops: shown, never asked for photos. */
  resolvedStops: ResolvedStopInfo[];
  statuses: MilestoneStatus[];
  /** Parallel to `pending`: a required signature still has no photo. */
  signatureMissing: boolean[];
  photos: PhotoMap;
  texts: TextMap;
  uploading: Record<string, number>;
  phase: Record<string, string>;
  fieldErrors: Record<string, string>;
  failedFiles: Record<string, File[]>;
  trimHints: Record<string, string>;
  saved: SavedEntry[];
  savedStripRef: React.RefObject<HTMLDivElement | null>;
  expandedKey: string | null;
  onToggle: (key: string) => void;
  disabled: boolean;
  onFiles: (
    key: string,
    field: ShipmentRouteField,
    files: FileList | null
  ) => void;
  onRetry: (key: string) => void;
  onRemove: (key: string, url: string) => void;
  onText: (key: string, value: string) => void;
}

/**
 * The 4W workflow-evidence section. The first pending milestone (where the
 * driver is stuck) is pinned open at the top; every other pending milestone is
 * a compact row that expands one at a time.
 */
const EvidenceSection: React.FC<EvidenceSectionProps> = (props) => {
  const {
    loading,
    loadError,
    onReload,
    pending,
    resolvedStops,
    statuses,
    signatureMissing,
    saved,
    savedStripRef,
  } = props;

  if (loading) {
    return (
      <div className="rounded-gsm border border-stroke-soft-200 bg-bg-weak-50 px-3 py-2 text-[13px] text-text-sub-600">
        Memuat workflow evidence…
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-gsm border border-stroke-soft-200 px-3 py-2 text-[13px] text-text-sub-600">
        <span role="alert">Gagal memuat bukti. Coba lagi.</span>
        <Button tone="neutral" style="stroke" size="xs" onClick={onReload}>
          Muat ulang
        </Button>
      </div>
    );
  }

  const [pinned, ...rest] = pending;

  return (
    <div
      data-testid="evidence-section"
      className="space-y-3 rounded-gsm border border-stroke-soft-200 p-3"
    >
      <p className="label-eyebrow">Workflow evidence</p>

      {resolvedStops.length > 0 && (
        <ul data-testid="evidence-resolved-stops" className="space-y-1">
          {resolvedStops.map((info) => (
            <li
              key={info.stopId}
              data-testid={`evidence-resolved-stop-${info.stopSequence}`}
              className="flex items-start gap-1.5 text-[13px] text-text-strong-950"
            >
              <i
                aria-hidden
                className={`mt-px text-[14px] leading-none ${
                  info.kind === 'FAILED'
                    ? 'ri-close-circle-line text-(--state-error-base)'
                    : 'ri-forbid-2-line text-text-sub-600'
                }`}
              />
              <span>
                Stop {info.stopSequence} · {info.label}
              </span>
            </li>
          ))}
        </ul>
      )}

      <div aria-live="polite">
        {saved.length > 0 && (
          <div
            ref={savedStripRef}
            tabIndex={-1}
            data-testid="evidence-saved"
            className="space-y-1 rounded-gsm bg-(--state-success-lighter) px-3 py-2 text-[12px] text-(--state-success-dark) outline-none"
          >
            <p className="font-semibold">Tersimpan barusan</p>
            {saved.map((s) => (
              <p key={`${s.key}@${s.at.getTime()}`} className="tabular-nums">
                {s.name} ✓ {hhmm(s.at)}
              </p>
            ))}
            {/* TODO(driver-app v3.6.1+): drop this once every 4W driver runs an
                app with code-based 409 auto-advance (kotlin-driver-mobile #393). */}
            <p>
              Minta driver menutup lalu membuka lagi layar trip untuk lanjut.
            </p>
          </div>
        )}
      </div>

      {pending.length === 0 ? (
        <p
          data-testid="evidence-all-complete"
          className="text-[13px] text-(--state-success-dark)"
        >
          ✓ Semua bukti foto sudah lengkap. Isi alasan untuk menandai shipment
          selesai.
        </p>
      ) : (
        <>
          <div
            data-testid={`evidence-milestone-${pinned.milestoneKey}`}
            className="space-y-2 rounded-gsm bg-bg-weak-50 p-3"
          >
            <p className="text-[11px] font-semibold text-text-sub-600">
              Driver berhenti di sini
            </p>
            <div className="flex items-start justify-between gap-2">
              <p className="text-[13px] font-semibold text-text-strong-950">
                Stop {pinned.stopSequence} · {pinned.milestoneName}
              </p>
              {signatureMissing[0] && (
                <Badge status="warning" className="shrink-0">
                  {SIGNATURE_COPY.chip}
                </Badge>
              )}
            </div>
            <MilestoneFields pm={pinned} {...props} />
          </div>

          {rest.map((pm, i) => {
            const key = milestonePrefix(pm.stopId, pm.milestoneKey);
            const expanded = props.expandedKey === key;
            // A missing signature is the specific reason this stop is stuck.
            const badge = signatureMissing[i + 1]
              ? { status: 'warning' as const, label: SIGNATURE_COPY.chip }
              : MILESTONE_BADGE[statuses[i + 1]];
            return (
              <div
                key={key}
                data-testid={`evidence-milestone-${pm.milestoneKey}`}
                className="border-t border-stroke-soft-200 pt-2"
              >
                <button
                  type="button"
                  aria-expanded={expanded}
                  onClick={() => props.onToggle(key)}
                  className="flex w-full items-center justify-between gap-2 text-left"
                >
                  <span className="text-[13px] font-semibold text-text-strong-950">
                    Stop {pm.stopSequence} · {pm.milestoneName}
                  </span>
                  <span className="flex items-center gap-2">
                    <Badge status={badge.status}>{badge.label}</Badge>
                    <i
                      aria-hidden
                      className={
                        expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'
                      }
                    />
                  </span>
                </button>
                {expanded && (
                  <div className="mt-2">
                    <MilestoneFields pm={pm} {...props} />
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

/**
 * Every field of one milestone: a photo slot with a count badge for each file
 * field (IMAGE, or SIGNATURE = one photo of the signed delivery note), an input
 * for TEXT / NUMBER.
 */
const MilestoneFields: React.FC<
  { pm: PendingPhotoMilestone } & EvidenceSectionProps
> = ({
  pm,
  photos,
  texts,
  uploading,
  phase,
  fieldErrors,
  failedFiles,
  trimHints,
  disabled,
  onFiles,
  onRetry,
  onRemove,
  onText,
}) => (
  <div className="space-y-3">
    {pm.fields.map((f) => {
      const key = slotKey(pm.stopId, pm.milestoneKey, f.fieldKey);
      const inputId = `evi-${key}`;
      if (!isFileFieldType(f.type)) {
        return (
          <Field key={key} data-testid={`evidence-field-${f.fieldKey}`}>
            <Label htmlFor={inputId} required={f.required}>
              {f.label}
            </Label>
            <InputRoot size="md">
              <Input
                id={inputId}
                type={f.type === 'NUMBER' ? 'number' : 'text'}
                inputMode={f.type === 'NUMBER' ? 'decimal' : undefined}
                value={texts[key] ?? ''}
                disabled={disabled}
                onChange={(e) => onText(key, e.target.value)}
              />
            </InputRoot>
          </Field>
        );
      }

      const urls = photos[key] ?? [];
      const need = requiredPhotoCount(f);
      const inFlight = uploading[key] ?? 0;
      const isSignature = f.type === 'SIGNATURE';
      const showSlotHint = isSignature && urls.length === 0 && inFlight === 0;
      const error = fieldErrors[key];
      const status = fieldStatus(f, {
        photos: { [f.fieldKey]: urls },
        texts: {},
      });
      const badgeStatus = error
        ? 'error'
        : status === 'complete'
          ? 'success'
          : status === 'partial'
            ? 'warning'
            : 'neutral';
      const room = Math.max(0, need - urls.length - inFlight);
      return (
        <div
          key={key}
          data-testid={`evidence-field-${f.fieldKey}`}
          className="space-y-1.5"
        >
          <div className="flex items-start justify-between gap-3">
            <label
              htmlFor={inputId}
              className="block text-[12px] text-text-sub-600"
            >
              {f.label}
              {!f.required && ' (opsional)'}
            </label>
            <span
              aria-live="polite"
              data-testid={`evidence-count-${f.fieldKey}`}
            >
              <Badge status={badgeStatus} className="tabular-nums">
                {error
                  ? 'Gagal'
                  : `${urls.length}/${need} foto${status === 'complete' ? ' ✓' : ''}`}
              </Badge>
            </span>
          </div>
          {/* The empty signature slot is a second label for the picker, so the
              dashed box opens it like a drop zone would. */}
          {showSlotHint && (
            <label
              htmlFor={inputId}
              className={`block rounded-gsm border border-dashed border-stroke-soft-200 px-3 py-2 text-[12px] text-text-sub-600 ${
                disabled
                  ? 'cursor-not-allowed'
                  : 'cursor-pointer hover:bg-bg-weak-50'
              }`}
            >
              {SIGNATURE_COPY.slot}
            </label>
          )}
          <input
            id={inputId}
            type="file"
            accept={isSignature ? SIGNATURE_ACCEPT : 'image/*'}
            multiple={need > 1}
            disabled={disabled || room === 0}
            onChange={(e) => {
              onFiles(key, f, e.target.files);
              e.target.value = '';
            }}
            className="block w-full text-[12px] text-text-sub-600 file:mr-3 file:rounded-gsm file:border file:border-stroke-soft-200 file:bg-bg-weak-50 file:px-3 file:py-1.5 file:text-[12px] file:font-semibold file:text-text-strong-950 hover:file:bg-bg-white-0 disabled:cursor-not-allowed"
          />
          {trimHints[key] && (
            <p className="text-[12px] text-text-sub-600">{trimHints[key]}</p>
          )}
          {error && (
            <div className="flex items-center gap-2">
              <p role="alert" className="text-[12px] text-(--state-error-dark)">
                {error}
              </p>
              {(failedFiles[key]?.length ?? 0) > 0 && (
                <Button
                  tone="neutral"
                  style="stroke"
                  size="xs"
                  disabled={disabled}
                  onClick={() => onRetry(key)}
                >
                  Coba lagi
                </Button>
              )}
            </div>
          )}
          {(urls.length > 0 || inFlight > 0) && (
            <div className="flex flex-wrap gap-2">
              {urls.map((url) => (
                <div
                  key={url}
                  className={`relative overflow-hidden rounded-gsm border border-stroke-soft-200 ${
                    isSignature ? 'h-14 w-28 bg-static-white' : 'h-14 w-14'
                  }`}
                >
                  <img
                    src={url}
                    alt={f.label}
                    className={`h-full w-full ${
                      isSignature ? 'object-contain' : 'object-cover'
                    }`}
                  />
                  <button
                    type="button"
                    aria-label={`Remove ${f.label} photo`}
                    disabled={disabled}
                    onClick={() => onRemove(key, url)}
                    className="absolute right-0 top-0 flex h-4 w-4 items-center justify-center bg-black/60 text-[10px] leading-none text-white"
                  >
                    <i className="ri-close-line" />
                  </button>
                </div>
              ))}
              {Array.from({ length: inFlight }).map((_, i) => (
                <div
                  key={`up-${i}`}
                  className={`flex h-14 items-center justify-center rounded-gsm border border-dashed border-stroke-soft-200 px-1 text-center text-[10px] text-text-sub-600 ${
                    isSignature ? 'w-28' : 'w-14'
                  }`}
                >
                  {phase[key] ?? '…'}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    })}
  </div>
);

export default ForceTerminalModal;
